
import React, { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSchool } from '../context/SchoolContext';
import {
  Award,
  BarChart2,
  Bell,
  Book,
  BookOpen,
  Calendar,
  CalendarCheck,
  CalendarDays,
  CheckSquare,
  ClipboardList,
  Clock,
  DollarSign,
  FileEdit,
  FileText,
  GraduationCap,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageCircle,
  Search,
  Settings,
  Upload,
  UserCog,
  Users,
} from 'lucide-react';
import { resolveAssetUrl } from '../features/settings/services/settingsResponse';

const MENU_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/students', label: 'Students', icon: Users },
  { path: '/staff', label: 'Staff', icon: UserCog },
  {
    path: '/fees',
    label: 'Fees',
    icon: DollarSign,
    subItems: [
      { path: '/fees/management', label: 'Fee Management' },
      { path: '/fees/structure', label: 'Fee Structure' },
      { path: '/fees/collection', label: 'Fee Collection' },
    ],
  },
  // {
  //   path: '/academics',
  //   label: 'Academics',
  //   icon: GraduationCap,
  //   subItems: [
  //     { path: '/academics/dashboard', label: 'Academic Dashboard', icon: LayoutDashboard },
  //     { path: '/academics/timetable', label: 'Timetable', icon: Clock },
  //     { path: '/academics/attendance', label: 'Attendance', icon: CalendarCheck },
  //     { path: '/academics/attendance-reports', label: 'Attendance Reports', icon: BarChart2 },
  //     { path: '/academics/exam-management', label: 'Exam Management', icon: BookOpen },
  //     { path: '/academics/exam-schedule', label: 'Exam Schedule', icon: Calendar },
  //     { path: '/academics/hall-tickets', label: 'Hall Tickets', icon: FileText },
  //     { path: '/academics/marks-entry', label: 'Marks Entry', icon: ClipboardList },
  //     { path: '/academics/marks-upload', label: 'Marks Upload', icon: Upload },
  //     { path: '/academics/report-cards', label: 'Report Cards', icon: Award },
  //     {
  //       path: '/academics/report-card-approval',
  //       label: 'Report Card Approval',
  //       icon: CheckSquare,
  //     },
  //     { path: '/academics/calendar-events', label: 'Calendar & Events', icon: CalendarDays },
  //     { path: '/academics/homework-diary', label: 'Homework & Diary', icon: Book },
  //     { path: '/academics/student-remarks', label: 'Student Remarks', icon: MessageCircle },
  //     { path: '/academics/handbook-builder', label: 'Handbook Builder', icon: FileEdit },
  //     { path: '/academics/reports-center', label: 'Reports Center', icon: BarChart2 },
  //   ],
  // },
  // { path: '/reports', label: 'Reports', icon: FileText },
  {
    path: '/documents',
    label: 'Documents',
    icon: FileText,
    subItems: [
      { path: '/documents/id-cards', label: 'ID Cards' },
      { path: '/documents/templates', label: 'Templates' },
      { path: '/documents/generation-jobs', label: 'Generation Jobs' },
      { path: '/documents/card-history', label: 'Card History' },
    ],
  },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const DashboardLayout = () => {
  const { user, activeRole, logout, isSuperuser } = useAuth();
  const {
    schoolId,
    schools,
    schoolsLoading,
    selectSchool,
    schoolProfile,
    branding,
    academicYears,
    activeAcademicYearId,
    academicYearLoading,
    academicYearError,
    setActiveAcademicYear,
  } = useSchool();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const schoolName =
    schoolProfile?.short_name ||
    schoolProfile?.school_name ||
    user?.school_name ||
    'EduAdmin';
  const schoolLogo = resolveAssetUrl(branding?.logo);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-sans text-gray-900">
      <aside className="flex w-64 shrink-0 flex-col border-r border-gray-200 bg-white">
        <div className="shrink-0 border-b border-gray-100 px-4 py-4">
          <div className="flex items-center">
            <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-blue-600 text-white">
              {schoolLogo ? (
                <img
                  src={schoolLogo}
                  alt={`${schoolName} logo`}
                  className="h-full w-full bg-white object-contain"
                />
              ) : (
                <GraduationCap className="h-6 w-6" />
              )}
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold leading-tight text-gray-900">
                {schoolName}
              </h1>
              <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                Working context
              </p>
            </div>
          </div>

          {isSuperuser && (
            <label className="mt-4 block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                School
              </span>
              <select
                value={schoolId}
                disabled={schoolsLoading}
                onChange={(event) => selectSchool(event.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-xs font-medium text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100"
              >
                {!schoolId && <option value="">Select school</option>}
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.short_name || school.school_name || school.name || school.id}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Academic Year
            </span>
            <select
              value={activeAcademicYearId}
              disabled={!schoolId || academicYearLoading || academicYears.length === 0}
              onChange={(event) => setActiveAcademicYear(event.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-xs font-semibold text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100 disabled:text-gray-400"
            >
              {academicYearLoading ? (
                <option value="">Loading years...</option>
              ) : academicYears.length === 0 ? (
                <option value="">No academic year</option>
              ) : (
                academicYears.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.year_name || year.name}{year.is_current ? ' (Current)' : ''}
                  </option>
                ))
              )}
            </select>
            {academicYearError && (
              <p className="mt-1 text-[11px] leading-4 text-red-600">{academicYearError}</p>
            )}
          </label>
        </div>

        <div className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
          {MENU_ITEMS.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            const Icon = item.icon;

            if (item.subItems) {
              return (
                <div key={item.path} className="flex flex-col gap-1">
                  <Link
                    to={item.path}
                    className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'cursor-pointer text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5" />
                      {item.label}
                    </div>
                  </Link>
                  {isActive && (
                    <div className="mt-1 flex flex-col gap-1 pl-6 pr-2">
                      {item.subItems.map((sub) => {
                        const SubIcon = sub.icon;
                        const isSubActive = location.pathname === sub.path;
                        return (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                              isSubActive
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            {SubIcon && <SubIcon className="h-4 w-4" />}
                            {sub.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </div>

        <div className="shrink-0 border-t border-gray-200 p-4">
          <div className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <HelpCircle className="h-4 w-4" />
            </div>
            <div>
              <p className="font-medium leading-tight text-gray-900">Need help?</p>
              <p className="text-xs leading-tight text-gray-500">Check our documentation</p>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6">
          <div className="max-w-xl flex-1">
            {/* <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search students, fees..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div> */}
          </div>

          <div className="ml-4 flex items-center gap-6">
            {/* <div className="flex items-center gap-4 text-gray-500">
              <button type="button" className="relative hover:text-gray-700">
                <Bell className="h-5 w-5" />
                <span className="absolute right-0 top-0 h-2 w-2 rounded-full border border-white bg-red-500" />
              </button>
              <button type="button" className="hover:text-gray-700">
                <Mail className="h-5 w-5" />
              </button>
            </div> */}

            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="flex items-center gap-3 border-l border-gray-200 pl-6"
                onClick={() => setIsDropdownOpen((current) => !current)}
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-sm font-medium text-white">
                  {user?.first_name ? user.first_name.charAt(0) : 'U'}
                  {user?.last_name ? user.last_name.charAt(0) : ''}
                </div>
                <div className="hidden text-left sm:block">
                  <p className="text-sm font-semibold leading-tight text-gray-900">
                    {user?.full_name || 'Admin User'}
                  </p>
                  <p className="text-xs leading-tight text-gray-500">
                    {activeRole?.name || 'Role'}
                  </p>
                </div>
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 z-50 mt-2 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={logout}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-gray-50"
                  >
                    <LogOut className="h-4 w-4" /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-white p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
